import Modal from '@/components/Modal';
import Settings from '@/components/buy-stack/ProductSelectSettings';
import ComponentToggleSignal from '@/components/component-toggle-signal';
import SelectorItemBox from '@/experiments/product-select-v3/SelectorItemBox';
import {ProductVariantOption} from '@/product/types';
import {
  formatColorName,
  getOptionSettings,
} from '@/settings/product-option-settings';
import {createSrcSet} from '@/utilities/create-image-src-set';
import {useState} from 'preact/hooks';

/**
 * Selector for product options.
 * This is the first (n) steps of the ProductSelectSection. It allows the user to select
 * between different options for a product, such as color or size.
 * The colors of the option buttons may be defined in the ProductOptionSelectorSettings.
 */
export default function ProductOptionSelector({
  onUpdate,
  options,
  title,
  value,
}: ProductOptionSelectorProps) {
  const [selected, setSelected] = useState(value ?? options[0]);

  const handleSelect = (option: ProductVariantOption) => {
    setSelected(option);
    onUpdate && onUpdate(option);
  };

  const {
    image: {alt, url},
  } = selected;

  const modalController = new ComponentToggleSignal();
  return (
    <Settings.Consumer>
      {({product_options_settings_map}) => {
        const selectedOptionSettings = getOptionSettings(
          selected,
          title,
          product_options_settings_map
        );
        const description = selectedOptionSettings?.description ?? '';
        return (
          <>
            <div className="grid-cols-max-grow grid items-center gap-4 justify-self-stretch md:grid-cols-1">
              {title && (
                <h6 className="col-span-full text-center text-sm font-semibold uppercase">
                  {title}:
                </h6>
              )}
              <img
                alt={alt}
                className="justify-self-center md:hidden"
                onClick={() => modalController.open()}
                src={url}
                srcSet={createSrcSet(url, 200)}
                sizes="(min-width: 48rem) 0, 50vw"
                width="88"
              />
              <div className="grid grid-cols-1 gap-y-3">
                {options?.map((option: ProductVariantOption) => {
                  const isSelectedOption =
                    selected.optionValue === option.optionValue;
                  const optionSettings = getOptionSettings(
                    option,
                    title,
                    product_options_settings_map
                  );
                  const color = formatColorName(
                    optionSettings?.color ?? 'primary-blue'
                  );
                  return (
                    <SelectorItemBox
                      color={color}
                      isSelected={isSelectedOption}
                      key={option.optionValue}
                      dataTestid={option.optionValue}
                      label={
                        <span className="w-full text-left text-base font-normal md:text-center">
                          {option.optionValue}
                        </span>
                      }
                      onClick={() => handleSelect(option)}
                    />
                  );
                })}
              </div>
              <p className="col-span-full px-3 text-center text-neutral-dark-gray">
                {description}
              </p>
            </div>
            <Modal controller={modalController}>
              <div className="flex h-full items-center">
                <img
                  src={url}
                  alt={alt}
                  className="fit-inside-modal"
                  srcSet={createSrcSet(url, 600)}
                  loading="lazy"
                />
              </div>
            </Modal>
          </>
        );
      }}
    </Settings.Consumer>
  );
}

export type ProductOptionSelectorProps = {
  onUpdate?: (value: ProductVariantOption) => void;
  options: ProductVariantOption[];
  title: string;
  value?: ProductVariantOption | null;
};
